/**
 * Générateur — Il simplifie une fraction pour la rendre irréductible.
 * Attendu ID : 206 | Niveau : 3ème | Domaine : Divisibilité (d10, O6)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A5S1-A5S2). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « reperer »        — repérer les fractions simplifiables (QCM multiple)
 *   « criteres »       — simplifier avec les critères de divisibilité (trous)
 *   « prouver »        — irréductible ? (tableau V/F CALCULÉ — adaptation :
 *                        la rédaction « prouve que » n'est pas scorable)
 *   « decompositions » — simplifier via les décompositions données (trous)
 *   « uneseule »       — UNE seule simplification : facteur commun maximal
 *   « ppcm »           — PPCM au service du calcul fractionnaire (trous)
 */

import {
    randomChoice, randInt, shuffle, tex, fracTex,
    makeQcm, makeTexteTrou, makeVraiFaux,
} from '../../../utils';
import { decomposer, decompFrag, pgcd, ppcm, estPremier } from './divisibilite';

const ATTENDU_ID = 206;

const L_REPERER = 'Repérer les fractions simplifiables';
const L_CRITERES = 'Simplifier avec les critères de divisibilité';
const L_PROUVER = 'Décider si une fraction est irréductible';
const L_DECOMP = 'Simplifier grâce aux décompositions';
const L_UNESEULE = 'Simplifier en une seule étape (facteur commun maximal)';
const L_PPCM = 'Utiliser le plus petit dénominateur commun';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « reperer » — QCM multiple ═════════

const genRepererQcm = () => {
    const vus = new Set();
    const simplifiables = [];
    for (let essai = 0; simplifiables.length < 3 && essai < 80; essai++) {
        const g = randomChoice([2, 3, 3, 5, 7]);
        const p = randInt(1, 12); const q = randInt(2, 15);
        if (pgcd(p, q) !== 1 || p === q) continue;
        const aff = fracTex(p * g, q * g);
        if (!vus.has(aff)) { vus.add(aff); simplifiables.push(aff); }
    }
    const irred = [];
    for (let essai = 0; irred.length < 2 && essai < 80; essai++) {
        const p = randomChoice([1, 3, 5, 7, 11, 13, 17, 19, 23]);
        const q = randInt(p + 1, 95);
        if (pgcd(p, q) !== 1) continue;
        const aff = fracTex(p, q);
        if (!vus.has(aff)) { vus.add(aff); irred.push(aff); }
    }
    // Replis déterministes (probabilité infime de boucles sèches)
    for (const [a, b] of [[4, 6], [15, 30], [42, 39]]) {
        const def = fracTex(a, b);
        if (simplifiables.length < 3 && !vus.has(def)) { vus.add(def); simplifiables.push(def); }
    }
    for (const [a, b] of [[3, 19], [1, 82]]) {
        const def = fracTex(a, b);
        if (irred.length < 2 && !vus.has(def)) { vus.add(def); irred.push(def); }
    }
    const payload = makeQcm(shuffle([...simplifiables, ...irred]), simplifiables,
        '<p><b>Sélectionne les trois fractions que l\'on peut simplifier.</b></p>',
        opts(L_REPERER), true);
    payload._meta.params = { simplifiables, irred };
    return payload;
};

// ═════════ « criteres » — simplifier (2 trous) ═════════

const genCriteresTrous = () => {
    let p; let q; let k; let N; let D;
    for (let essai = 0; essai < 80; essai++) {
        p = randInt(2, 23); q = randInt(2, 23); k = randomChoice([3, 5, 6, 9, 10, 11, 15, 30]);
        N = p * k; D = q * k;
        if (pgcd(p, q) === 1 && p !== q && N <= 450 && D <= 450 && N !== D) break;
        N = null;
    }
    if (!N) { p = 11; q = 5; k = 6; N = 66; D = 30; } // repli (doc Ex.6)
    const phrase = `$\\dfrac{${N}}{${D}} = \\dfrac{{{${p}}}}{{{${q}}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p>Aide-toi des critères de divisibilité.</p>'
        + `<p><b>Rends la fraction ${fracTex(N, D)} irréductible.</b></p>`,
        { ...opts(L_CRITERES), mode: 'saisie' });
    payload._meta.params = { p, q, k };
    return payload;
};

// ═════════ « prouver » — V/F calculé ═════════

const GRANDS_PREMIERS = [211, 307, 383, 431, 439, 547, 613, 673];

const genProuverVf = () => {
    const irreductible = randInt(0, 1) === 1;
    let n; let d;
    if (irreductible) {
        // Dénominateur PREMIER plus grand que n → aucun facteur commun.
        d = randomChoice(GRANDS_PREMIERS);
        n = randomChoice([2, 3]) * randomChoice([7, 11, 13, 17, 19, 23, 29, 31, 37]);
    } else {
        // Facteur commun caché g, dénominateur composé.
        const g = randomChoice([2, 3, 7]);
        const b = randomChoice([89, 97, 101, 103, 107, 109, 113]);
        d = g * b;
        n = g * randInt(11, 45);
        if (pgcd(n / g, b) !== 1) n = g * 13;
    }
    const commun = pgcd(n, d) > 1;
    const lignes = [
        `${d} est un nombre premier.`,
        `${n} et ${d} ont un diviseur commun plus grand que 1.`,
        `La fraction ${fracTex(n, d)} est irréductible.`,
    ];
    const bonnes = [estPremier(d) ? 0 : 1, commun ? 0 : 1, commun ? 1 : 0];
    const ordre = shuffle([0, 1, 2]);
    const payload = makeVraiFaux(ordre.map(i => lignes[i]), ordre.map(i => bonnes[i]),
        // « Pense à décomposer » donnait la méthode attendue.
        `<p>On s'intéresse à la fraction ${fracTex(n, d)}.</p><p><b>Vrai ou faux ?</b></p>`,
        opts(L_PROUVER));
    payload._meta.params = { n, d, bonnes };
    return payload;
};

// ═════════ « decompositions » — via les décompositions données ═════════

const genDecompTrous = () => {
    let N; let D; let num; let den;
    for (let essai = 0; essai < 80; essai++) {
        const a1 = randInt(1, 3); const b1 = randInt(1, 3); const a2 = randInt(1, 3); const b2 = randInt(1, 3);
        const [pExtra, qExtra] = shuffle([5, 7]).slice(0, 2);
        N = 2 ** a1 * 3 ** b1 * pExtra;
        D = 2 ** a2 * 3 ** b2 * qExtra;
        const g = pgcd(N, D);
        num = N / g; den = D / g;
        if (g > 1 && den > 1 && num <= 99 && den <= 99 && N <= 999 && D <= 999 && N !== D) break;
        num = null;
    }
    if (!num) { N = 504; D = 540; num = 14; den = 15; } // repli (doc Ex.9)
    const phrase = `$\\dfrac{${N}}{${D}} = \\dfrac{{{${num}}}}{{{${den}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On donne les décompositions : ${tex(`${N} = ${decompFrag(decomposer(N))}`)} et `
        + `${tex(`${D} = ${decompFrag(decomposer(D))}`)}.</p>`
        // « compare les exposants » était la méthode à trouver.
        + `<p><b>Rends ${fracTex(N, D)} irréductible sans tout recalculer.</b></p>`,
        { ...opts(L_DECOMP), mode: 'saisie' });
    payload._meta.params = { N, D, num, den };
    return payload;
};

// ═════════ « uneseule » — facteur commun maximal ═════════

const genUneSeuleTrous = () => {
    let p; let q; let g; let N; let D;
    for (let essai = 0; essai < 80; essai++) {
        p = randInt(2, 15); q = randInt(2, 15);
        g = randomChoice([2, 3]) * randomChoice([29, 31, 37, 41, 43, 47, 53]);
        N = p * g; D = q * g;
        if (pgcd(p, q) === 1 && p !== q && N <= 2000 && D <= 2000 && g !== p && g !== q) break;
        N = null;
    }
    if (!N) { p = 14; q = 3; g = 86; N = 1204; D = 258; } // repli (doc Ex.10)
    const phrase = `$\\text{Facteur commun maximal : } {{${g}}} \\qquad`
        + ` \\dfrac{${N}}{${D}} = \\dfrac{{{${p}}}}{{{${q}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Rends ${fracTex(N, D)} irréductible en effectuant UNE seule simplification.</b> `
        + 'Donne d\'abord le plus grand facteur commun au numérateur et au dénominateur.</p>',
        { ...opts(L_UNESEULE), mode: 'saisie' });
    payload._meta.params = { p, q, g };
    return payload;
};

// ═════════ « ppcm » — dénominateur commun minimal ═════════

// Domaine ALIGNÉ SUR LE BREVET (2026-07-28) : dénominateurs ≤ 20, PPCM ≤ 72.
// Relevé sur 20 sujets de DNB (2001-2002, tous groupements) : les opérandes ont
// des dénominateurs de 2 à 18, et les additions à dénominateurs différents ont
// un PPCM qui dépasse rarement 30. L'ancien domaine montait à 48 / 240, avec des
// numérateurs jusqu'à 417 avant simplification : de la gymnastique arithmétique,
// pas de la simplification — laquelle EST l'objet de l'attendu 206.
const PPCM_D_MAX = 20;
const PPCM_L_MAX = 72;

// Tirage CONSTRUCTIF et STRATIFIÉ. Deux faits l'imposent :
//
// 1. L'ancienne boucle par rejet n'exigeait jamais que la somme soit réductible :
//    80 % des tirages tombaient déjà irréductibles et l'élève n'avait donc RIEN
//    à simplifier, alors que c'est l'objet de l'attendu 206.
// 2. Mais on ne PEUT PAS exiger qu'elle le soit toujours. Si d1 et d2 sont
//    premiers entre eux, la somme de deux fractions irréductibles l'est aussi :
//    pour tout premier p divisant L = d1·d2, p divise d1 OU d2 seul, donc ne
//    divise ni n1·d2 ni… bref, jamais le numérateur. Les petits PPCM du brevet
//    (12, 15, 18, 20, 21, 24, 28) sont ainsi TOUS structurellement irréductibles,
//    et le plus petit cas simplifiable est 6/10 → PPCM 30.
//
// D'où deux pools tirés à 50/50 : la moitié des exercices exercent la
// simplification, l'autre moitié oblige à VÉRIFIER qu'elle est déjà
// irréductible — ce que la consigne « en fraction irréductible » demande de
// toute façon. On couvre ainsi 29 valeurs de PPCM (12 à 72) au lieu de 4.
const [PPCM_REDUCTIBLES, PPCM_IRREDUCTIBLES] = (() => {
    const red = []; const irr = [];
    for (let d1 = 3; d1 <= PPCM_D_MAX; d1++) {
        for (let d2 = d1 + 1; d2 <= PPCM_D_MAX; d2++) {
            const L = ppcm(d1, d2);
            // L === d1 ou d2 : un dénominateur est multiple de l'autre, la
            // recherche du PPCM devient triviale.
            if (L > PPCM_L_MAX || L === d1 || L === d2) continue;
            for (let n1 = 1; n1 < d1; n1++) {
                if (pgcd(n1, d1) !== 1) continue;          // fractions de départ irréductibles
                for (let n2 = 1; n2 < d2; n2++) {
                    if (pgcd(n2, d2) !== 1) continue;
                    ['+', '-'].forEach((op) => {
                        const num = op === '+'
                            ? n1 * (L / d1) + n2 * (L / d2)
                            : n1 * (L / d1) - n2 * (L / d2);
                        if (num === 0) return;
                        const gg = pgcd(Math.abs(num), L);
                        if (L / gg <= 1) return;            // résultat entier : hors sujet
                        (gg > 1 ? red : irr).push({ d1, d2, L, n1, n2, op, p: num / gg, q: L / gg });
                    });
                }
            }
        }
    }
    return [red, irr];
})();

const genPpcmTrous = () => {
    const t = randomChoice(randInt(0, 1) === 0 ? PPCM_REDUCTIBLES : PPCM_IRREDUCTIBLES);
    const phrase = `$\\text{Plus petit dénominateur commun : } {{${t.L}}} \\qquad`
        + ` \\text{résultat : } \\dfrac{{{${t.p}}}}{{{${t.q}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut calculer ${tex(`\\dfrac{${t.n1}}{${t.d1}} ${t.op === '+' ? '+' : '-'} \\dfrac{${t.n2}}{${t.d2}}`)}.</p>`
        + '<p><b>Donne le plus petit multiple commun des dénominateurs, puis le résultat '
        + 'en fraction irréductible.</b> Si le résultat est négatif, écris le signe au numérateur.</p>',
        { ...opts(L_PPCM), mode: 'saisie' });
    payload._meta.params = { ...t };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    reperer_qcm_multiple: genRepererQcm,
    criteres_texte_trou_saisie: genCriteresTrous,
    prouver_tableau_vf: genProuverVf,
    decompositions_texte_trou_saisie: genDecompTrous,
    uneseule_texte_trou_saisie: genUneSeuleTrous,
    ppcm_texte_trou_saisie: genPpcmTrous,
};

const FORMAT_LABELS = {
    qcm_multiple: 'QCM Multi', texte_trou_saisie: 'Saisie trous', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'reperer', label: L_REPERER, formatsList: buildFormats('reperer') },
    { id: 'criteres', label: L_CRITERES, formatsList: buildFormats('criteres') },
    { id: 'prouver', label: L_PROUVER, formatsList: buildFormats('prouver') },
    { id: 'decompositions', label: L_DECOMP, formatsList: buildFormats('decompositions') },
    { id: 'uneseule', label: L_UNESEULE, formatsList: buildFormats('uneseule') },
    { id: 'ppcm', label: L_PPCM, formatsList: buildFormats('ppcm') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
